import React from 'react';
import PropTypes from 'prop-types';
import './ProjectSummary.scss';

const renderWorkLocation = (workLocation, shortOfficeLocation) => {
  switch (workLocation.toLowerCase()) {
    case 'office':
      return `In Office at ${shortOfficeLocation}`;

    case 'remote':
      return 'Remote';

    case 'hybrid':
      return `Hybrid, in office at ${shortOfficeLocation}`;

    case 'not sure':
      return 'Workplace Type - Unsure';

    case "i don't know yet":
      return 'Workplace Type - Unsure';

    default:
      return 'Work preference';
  }
};

const renderHourlyRate = (minHourlyRate, maxHourlyRate) => (
  <div className="commitment hourly-rate">
    <i className="fa fa-dollar-sign" />
    {`$${minHourlyRate}`}
    {minHourlyRate && maxHourlyRate && ' - '}
    {maxHourlyRate ? `$${maxHourlyRate}/hour` : '/hour'}
  </div>
);

const renderTravel = (travelTime, daysOfTravel, travelLocations, location) => {
  if (daysOfTravel && travelLocations) {
    return (
      <div className="commitment travel">
        <i className="fa fa-plane" />
        Travel to
        {' '}
        {travelLocations}
        {' '}
        {daysOfTravel}
        {' '}
        days/month
      </div>
    );
  } if (travelLocations) {
    return (
      <div className="commitment travel">
        <i className="fa fa-plane" />
        Travel -
        {' '}
        {travelLocations}
      </div>
    );
  } if (daysOfTravel) {
    return (
      <div className="commitment travel">
        <i className="fa fa-plane" />
        {daysOfTravel}
        {' '}
        days/month
      </div>
    );
  } if (location === 'default' && travelTime) {
    return (
      <div className="commitment travel">
        <i className="fa fa-plane" />
        {travelTime === 'not sure' ? 'Travel Requirements - Not confirmed' : 'Travel Requirements -  Not Applicable'}
      </div>
    );
  }
  return null;
};

const renderTags = (tags, customClasses) => (
  tags.map((t) => (
    <span key={t} className={`badge small ${customClasses} normal-font`}>{t}</span>
  ))
);

function ProjectSummary(props) {
  const {
    name,
    workCommitment,
    areaOfExpertises,
    tags,
    jobTitles,
    description,
    responsibilities,
    duration,
    workLocation,
    shortOfficeLocation,
    minHourlyRate,
    maxHourlyRate,
    startDate,
    daysOfTravel,
    travelTime,
    travelLocations,
    location,
  } = props;
  return (
    <div className="project-summary">
      <div className="project-header">
        <h1 className="demibold project-name mt-2">{name || 'Project Name'}</h1>
        <div className="body-text-1 muted-gray-text">{jobTitles && `Relevant Job Titles(s): ${jobTitles}`}</div>
      </div>

      <div className="project-commitments body-text-3">
        {shortOfficeLocation
          && (
          <div className="commitment work-location">
            <i className="fas  fa-map-marker-alt" />
            {renderWorkLocation(workLocation, shortOfficeLocation)}
          </div>
          )}
        {duration
          && (
          <div className="commitment work-duration">
            <i className="fas fa-calendar-day" />
            {duration === 'not sure' ? 'Length of engagement - Not confirmed' : `${duration} month duration`}
          </div>
          )}
        {(minHourlyRate || maxHourlyRate) && renderHourlyRate(minHourlyRate, maxHourlyRate)}
        {workCommitment
          && (
          <div className="commitment work-commitment">
            <i className="fas fa-clock" />
            {workCommitment === 'not sure' ? 'Hours/Week - Not confirmed' : `${workCommitment} hours/week`}
          </div>
          )}
        {startDate
          ? (
            <div className="commitment approximate-start-date-local">
              <i className="fa fa-hourglass-start" />
              Starting
              {' '}
              {window.moment(startDate) < new Date() ? 'ASAP' : window.moment(startDate).format('LL')}
            </div>
          ) : ''}
        {!startDate && location === 'default'
          ? (
            <div className="commitment approximate-start-date-local">
              <i className="fa fa-hourglass-start" />
              Starting date - Not confirmed
            </div>
          ) : ''}
        {renderTravel(travelTime, daysOfTravel, travelLocations, location)}
      </div>

      {(areaOfExpertises.length > 0 || tags.length) > 0
        && (
        <div className="tags mb-2 d-flex flex-wrap gap-2">
          {renderTags(areaOfExpertises, 'purple-base')}
          {renderTags(tags, '')}
        </div>
        )}

      <div className="project-description body-text-2">
        {description || 'Description...'}
      </div>

      {responsibilities && responsibilities.length > 0
        && (
        <div className="project-responsibilities">
          <h2 className="medium-weight mb-2">
            Responsibilities
          </h2>
          <div>
            <ul className="responsibility_areas dashed">
              {responsibilities.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        </div>
        )}
    </div>
  );
}

ProjectSummary.propTypes = {
  name: PropTypes.string.isRequired,
  workCommitment: PropTypes.number.isRequired,
  jobTitles: PropTypes.string,
  description: PropTypes.string.isRequired,
  responsibilities: PropTypes.arrayOf(PropTypes.string).isRequired,
  areaOfExpertises: PropTypes.arrayOf(PropTypes.string).isRequired,
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
  duration: PropTypes.string.isRequired,
  workLocation: PropTypes.string.isRequired,
  shortOfficeLocation: PropTypes.string.isRequired,
  minHourlyRate: PropTypes.string,
  maxHourlyRate: PropTypes.string,
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  daysOfTravel: PropTypes.string,
  travelTime: PropTypes.string,
  travelLocations: PropTypes.string,
  location: PropTypes.string,
};

ProjectSummary.defaultProps = {
  jobTitles: null,
  travelTime: null,
  location: 'default',
};

export default ProjectSummary;
